// The Earth's finest imagery level, from Sentinel-2 cloudless 2016 by EOX
// (10 m, Creative Commons Attribution 4.0, so it may ship in the paid builds
// with the credit below; the later years are non-commercial and are not used).
//
// EOX serves it on its own lat/lon grid (WGS84 matrix: 180 / 2^k degrees per
// 256 px tile), offset from the Landsat pyramid's (288 / 2^z per 512 px), but
// both are plate carrée, so a Landsat-grid tile is the EOX tiles under it
// drawn at a scale and an offset: no reprojection. Each composed tile is
// colour-fitted to Landsat around it (fitField below), so the level change
// reads as sharpening rather than a new photograph. The composed JPEG goes
// into the streamer's Cache Storage like any other tile (planetimagery.js
// compose).
//
// The imagery is a 2016 mosaic over a 1999 one: places built in between
// (new districts, reservoirs) appear as the camera reaches this level.

export const S2_ATTRIBUTION = 'Sentinel-2 cloudless - https://s2maps.eu by EOX IT Services GmbH (Contains modified Copernicus Sentinel data 2016)';
export const S2_CREDIT_URL = 'https://s2maps.eu';

const S2_LEVEL = 13; // ~9.5 m per pixel at the equator: the data's own resolution
const S2_SPAN = 180 / 2 ** S2_LEVEL;
const S2_TILE = 256;
const s2Url = (row, col, level = S2_LEVEL) => `https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless/default/WGS84/${level}/${row}/${col}.jpg`;
// EOX fills the sea away from the coast with one flat colour (a 116-byte
// tile offshore, and the same colour round the data in a coastal one). It
// is no measurement of the water, so it takes no part in the fit and the
// Landsat under it shows there instead.
const S2_FILL = [24, 44, 78];
const isFill = (d, i) => Math.abs(d[i] - S2_FILL[0]) + Math.abs(d[i + 1] - S2_FILL[1]) + Math.abs(d[i + 2] - S2_FILL[2]) <= 9;

// The colour fit. A gain and an offset per channel matched one tile at a
// time drew every tile in its own colour: the statistics of a 7.8 km square
// are whatever share of sea, sand and town it holds, and the 1999 and 2016
// mosaics hold different ones (land reclaimed in between, EOX's clipped
// desert), so neighbours came out lighter or more violet with a straight
// seam between them, and a gain of up to 2 pushed towns to rust and their
// gardens to blue. The fit is now a field: the gain and offset at a point
// match the mean and spread of each channel over the ground round that point
// (FIT_RADIUS), from Landsat and from EOX's own coarser level, both at about
// 61 m, then smoothed (FIT_SMOOTH). It depends on the place alone, so two
// tiles compute the same values along the edge they share and meet without
// a seam. Matching moments (and not a regression of one image on the other)
// keeps the 2016 picture's own structure where the ground changed.
const FIT_WORK = 128;        // fit grid cells per composed tile side (61 m at level 12)
const FIT_RADIUS = 24;       // statistics window half-size in cells (about 1.5 km)
const FIT_SMOOTH = 16;       // smoothing half-size of the gains and offsets
const FIT_MARGIN = FIT_RADIUS + FIT_SMOOTH;
const FIT_S2_LEVEL = 10;     // EOX level for the statistics (180 / 2^10 degrees per 256 px, ~76 m)
const FIT_LANDSAT_LEVEL = 10; // Landsat level (288 / 2^10 degrees per 512 px: one fit cell per pixel)
// The spread is matched only part way: the finer level is meant to add
// contrast the coarser one averages away.
const FIT_SPREAD = 0.6;

// EOX requests in flight across every composition, so ten tiles composing at
// once do not open a few hundred requests.
const MAX_PARTS = 24;
let active = 0;
const waiting = [];
function limited(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      active++;
      task().then(resolve, reject).finally(() => { active--; if (waiting.length) waiting.shift()(); });
    };
    if (active < MAX_PARTS) run(); else waiting.push(run);
  });
}

function canvas(w, h) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}
function toBlob(c, quality) {
  if (c.convertToBlob) return c.convertToBlob({ type: 'image/jpeg', quality });
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', quality));
}

// Per-channel mean and spread over the pixels both images cover with data
// (Landsat's no-data is black). null when too little overlaps to trust.
const FIT = 64;
function channelStats(a, b) {
  const sa = [0, 0, 0], sb = [0, 0, 0], qa = [0, 0, 0], qb = [0, 0, 0];
  let n = 0;
  for (let i = 0; i < a.length; i += 4) {
    if (a[i] + a[i + 1] + a[i + 2] < 12 || b[i] + b[i + 1] + b[i + 2] < 12 || isFill(b, i)) continue;
    n++;
    for (let c = 0; c < 3; c++) { sa[c] += a[i + c]; sb[c] += b[i + c]; qa[c] += a[i + c] ** 2; qb[c] += b[i + c] ** 2; }
  }
  if (n < FIT * FIT * 0.25) return null;
  const out = [];
  for (let c = 0; c < 3; c++) {
    const ma = sa[c] / n, mb = sb[c] / n;
    out.push({ ma, mb, da: Math.sqrt(Math.max(1, qa[c] / n - ma * ma)), db: Math.sqrt(Math.max(1, qb[c] / n - mb * mb)) });
  }
  return out;
}

// Summed-area table of a w x w grid, one extra row and column of zeros.
function integral(values, w) {
  const out = new Float64Array((w + 1) * (w + 1));
  for (let y = 0; y < w; y++) {
    let row = 0;
    for (let x = 0; x < w; x++) {
      row += values[y * w + x];
      out[(y + 1) * (w + 1) + x + 1] = out[y * (w + 1) + x + 1] + row;
    }
  }
  return out;
}
function boxSum(sat, w, x0, y0, x1, y1) {
  x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(w, x1); y1 = Math.min(w, y1);
  const W = w + 1;
  return sat[y1 * W + x1] - sat[y0 * W + x1] - sat[y1 * W + x0] + sat[y0 * W + x0];
}

// The gain and offset field round a level-12 tile: per channel, on the fit
// grid (FIT_WORK cells across the tile plus FIT_MARGIN on every side, aligned
// to the tile grid so neighbouring tiles share cells). Sources are drawn into
// that grid from the tiles they cover; latitude past the poles stays empty.
// null when too little of it has data in both mosaics.
async function fitField(tile, imageryTile, fetchTile, signal) {
  const cell = tile.span / FIT_WORK, N = FIT_WORK + 2 * FIT_MARGIN;
  const west = -180 + tile.x * tile.span - FIT_MARGIN * cell, north = 90 - tile.y * tile.span + FIT_MARGIN * cell;
  const extent = N * cell;
  async function draw(span, size, read) {
    const c = canvas(N, N), ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const parts = [];
    for (let ty = Math.floor((90 - north) / span); ty <= Math.floor((90 - north + extent) / span - 1e-9); ty++) {
      if (ty < 0 || ty >= Math.round(180 / span)) continue;
      for (let tx = Math.floor((west + 180) / span); tx <= Math.floor((west + extent + 180) / span - 1e-9); tx++) parts.push({ tx, ty });
    }
    const images = await Promise.all(parts.map(({ tx, ty }) => read(tx, ty).catch(() => null)));
    signal?.throwIfAborted();
    parts.forEach(({ tx, ty }, i) => {
      if (!images[i]) return;
      const scale = 1 / cell;
      ctx.drawImage(images[i], 0, 0, size, size, (-180 + tx * span - west) * scale, (north - (90 - ty * span)) * scale, span * scale, span * scale);
      images[i].close?.();
    });
    return ctx.getImageData(0, 0, N, N).data;
  }
  const landsatSpan = 288 / 2 ** FIT_LANDSAT_LEVEL, s2Span = 180 / 2 ** FIT_S2_LEVEL, s2Cols = 2 ** (FIT_S2_LEVEL + 1);
  const [L, S] = await Promise.all([
    draw(landsatSpan, 512, async (tx, ty) => {
      const response = await fetchTile(imageryTile(FIT_LANDSAT_LEVEL, tx, ty), signal);
      if (!response?.ok) return null;
      return createImageBitmap(await response.blob());
    }),
    draw(s2Span, S2_TILE, (tx, ty) => limited(async () => {
      signal?.throwIfAborted();
      const response = await fetch(s2Url(ty, ((tx % s2Cols) + s2Cols) % s2Cols, FIT_S2_LEVEL), { signal, mode: 'cors' });
      if (!response.ok) return null;
      return createImageBitmap(await response.blob());
    })),
  ]);
  signal?.throwIfAborted();
  // Cells with data in both: Landsat's no-data is black, EOX's is its fill.
  const n = N * N, weight = new Float64Array(n);
  let usable = 0;
  for (let i = 0; i < n; i++) {
    const j = i * 4;
    const ok = L[j] + L[j + 1] + L[j + 2] >= 12 && S[j] + S[j + 1] + S[j + 2] >= 12 && !isFill(S, j);
    weight[i] = ok ? 1 : 0;
    usable += weight[i];
  }
  if (usable < n * 0.1) return null;
  const W = integral(weight, N);
  const gain = [], offset = [];
  const known = new Float64Array(n);
  for (let c = 0; c < 3; c++) {
    const v = new Float64Array(n);
    const sat = (f) => { for (let i = 0; i < n; i++) v[i] = weight[i] ? f(i * 4 + c) : 0; return integral(v, N); };
    const SI = sat((j) => S[j]), SI2 = sat((j) => S[j] * S[j]), SP = sat((j) => L[j]), SP2 = sat((j) => L[j] * L[j]);
    const a = new Float64Array(n), b = new Float64Array(n);
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
      const box = [x - FIT_RADIUS, y - FIT_RADIUS, x + FIT_RADIUS + 1, y + FIT_RADIUS + 1];
      const count = boxSum(W, N, ...box);
      const i = y * N + x;
      // Fewer than a sixth of the window's cells with data in both is not
      // enough to measure a spread; the smoothing fills those cells in.
      if (count < (2 * FIT_RADIUS + 1) ** 2 / 6) continue;
      const mI = boxSum(SI, N, ...box) / count, mP = boxSum(SP, N, ...box) / count;
      const dI = Math.sqrt(Math.max(1, boxSum(SI2, N, ...box) / count - mI * mI));
      const dP = Math.sqrt(Math.max(1, boxSum(SP2, N, ...box) / count - mP * mP));
      a[i] = Math.min(2, Math.max(0.5, 1 + (dP / dI - 1) * FIT_SPREAD));
      b[i] = mP - a[i] * mI;
      known[i] = 1;
    }
    gain.push(a); offset.push(b);
  }
  // Smooth over the cells that measured, so the field has no steps where a
  // window's count crosses its floor; with none measured nearby a cell keeps
  // the Sentinel-2 colour (gain 1, offset 0).
  const K = integral(known, N);
  const field = { gain: [], offset: [] };
  for (let c = 0; c < 3; c++) {
    const A = integral(gain[c], N), B = integral(offset[c], N);
    const ga = new Float32Array((FIT_WORK + 2) ** 2), ob = new Float32Array((FIT_WORK + 2) ** 2);
    // Only the tile's own cells and one beyond each edge are needed for the
    // bilinear read at its pixel centres.
    for (let y = 0; y < FIT_WORK + 2; y++) for (let x = 0; x < FIT_WORK + 2; x++) {
      const gx = x + FIT_MARGIN - 1, gy = y + FIT_MARGIN - 1;
      const box = [gx - FIT_SMOOTH, gy - FIT_SMOOTH, gx + FIT_SMOOTH + 1, gy + FIT_SMOOTH + 1];
      const k = boxSum(K, N, ...box);
      const i = y * (FIT_WORK + 2) + x;
      ga[i] = k > 0 ? boxSum(A, N, ...box) / k : 1;
      ob[i] = k > 0 ? boxSum(B, N, ...box) / k : 0;
    }
    field.gain.push(ga); field.offset.push(ob);
  }
  return field;
}

// compose(tile, signal) -> Response (image/jpeg) for a Landsat-grid tile.
// imageryTile(z, x, y) names a Landsat tile and fetchTile(tile, signal)
// brings its bytes (both from the streamer).
export function createS2Composer({ tileSize = 512, imageryTile, fetchTile }) {
  const parent = (tile) => imageryTile(tile.z - 1, Math.floor(tile.x / 2), Math.floor(tile.y / 2));
  return async function compose(tile, signal) {
    const lon0 = -180 + tile.x * tile.span, lat1 = 90 - tile.y * tile.span;
    const c0 = Math.floor((lon0 + 180) / S2_SPAN), c1 = Math.floor((lon0 + tile.span + 180) / S2_SPAN - 1e-9);
    const r0 = Math.floor((90 - lat1) / S2_SPAN), r1 = Math.floor((90 - lat1 + tile.span) / S2_SPAN - 1e-9);
    const parts = [];
    for (let row = r0; row <= r1; row++) for (let col = c0; col <= c1; col++) parts.push({ row, col });
    // The fit field and the Landsat tile above load beside the parts. They
    // settle to null on any failure, a cancellation included (checked once
    // they are awaited), so a composition dropped while the parts load
    // leaves no rejection unhandled.
    const fieldPromise = fitField(tile, imageryTile, fetchTile, signal).catch(() => null);
    const parentPromise = (async () => {
      const response = await fetchTile(parent(tile), signal);
      return response?.ok ? createImageBitmap(await response.blob()) : null;
    })().catch(() => null);
    const bitmaps = await Promise.all(parts.map(({ row, col }) => limited(async () => {
      signal?.throwIfAborted();
      const response = await fetch(s2Url(row, col), { signal, mode: 'cors' });
      if (!response.ok) throw new Error(`Sentinel-2 ${response.status}`);
      return createImageBitmap(await response.blob());
    })));

    const out = canvas(tileSize, tileSize);
    const ctx = out.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const scale = tileSize / tile.span;
    // Each part drawn a hair oversize so no seam of background shows between them.
    parts.forEach(({ row, col }, i) => {
      const x = (-180 + col * S2_SPAN - lon0) * scale, y = (lat1 - (90 - row * S2_SPAN)) * scale, w = S2_SPAN * scale;
      ctx.drawImage(bitmaps[i], 0, 0, S2_TILE, S2_TILE, x - 0.01, y - 0.01, w + 0.02, w + 0.02);
      bitmaps[i].close();
    });

    const [field, pb] = await Promise.all([fieldPromise, parentPromise]);
    signal?.throwIfAborted();
    // The Landsat quadrant above, at this tile's size: the stand-in for
    // EOX's fill, and the fallback fit's reference.
    let under = null;
    if (pb) {
      const half = tileSize / 2;
      const u = canvas(tileSize, tileSize).getContext('2d', { willReadFrequently: true });
      u.imageSmoothingEnabled = true;
      u.imageSmoothingQuality = 'high';
      u.drawImage(pb, (tile.x % 2) * half, (tile.y % 2) * half, half, half, 0, 0, tileSize, tileSize);
      pb.close();
      under = u;
    }
    const image = ctx.getImageData(0, 0, tileSize, tileSize);
    const d = image.data;
    let fit = null;
    if (field) {
      // Bilinear in the field at each pixel centre (the field holds the
      // tile's cells and one beyond each edge).
      const side = FIT_WORK + 2, k = FIT_WORK / tileSize;
      fit = (px, py, c, value) => {
        const u = (px + 0.5) * k + 0.5, v = (py + 0.5) * k + 0.5;
        const x0 = Math.min(side - 2, Math.floor(u)), y0 = Math.min(side - 2, Math.floor(v)), fx = u - x0, fy = v - y0;
        const i = y0 * side + x0, g = field.gain[c], o = field.offset[c];
        const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
        return value * (g[i] * w00 + g[i + 1] * w10 + g[i + side] * w01 + g[i + side + 1] * w11)
          + o[i] * w00 + o[i + 1] * w10 + o[i + side] * w01 + o[i + side + 1] * w11;
      };
    } else if (under) {
      // No field (EOX's coarse level or the Landsat round the tile did not
      // load): the older fit to the quadrant above, measured at 64 px.
      const a = canvas(FIT, FIT).getContext('2d', { willReadFrequently: true });
      a.drawImage(under.canvas, 0, 0, tileSize, tileSize, 0, 0, FIT, FIT);
      const b = canvas(FIT, FIT).getContext('2d', { willReadFrequently: true });
      b.drawImage(out, 0, 0, tileSize, tileSize, 0, 0, FIT, FIT);
      const stats = channelStats(a.getImageData(0, 0, FIT, FIT).data, b.getImageData(0, 0, FIT, FIT).data);
      if (stats) {
        const gain = stats.map((s) => Math.min(2, Math.max(0.5, 1 + (s.da / s.db - 1) * FIT_SPREAD)));
        const offset = stats.map((s, c) => s.ma - gain[c] * s.mb);
        fit = (px, py, c, value) => value * gain[c] + offset[c];
      }
    }
    // EOX's fill, feathered over a few pixels so the Landsat that replaces
    // it meets the fitted data without a step.
    const fill = new Float64Array(tileSize * tileSize);
    let anyFill = false;
    for (let i = 0, p = 0; i < d.length; i += 4, p++) if (isFill(d, i)) { fill[p] = 1; anyFill = true; }
    let feather = null;
    if (anyFill && under) {
      const F = integral(fill, tileSize), r = 3, area = (2 * r + 1) ** 2;
      feather = new Float32Array(tileSize * tileSize);
      for (let y = 0; y < tileSize; y++) for (let x = 0; x < tileSize; x++) {
        feather[y * tileSize + x] = Math.min(1, boxSum(F, tileSize, x - r, y - r, x + r + 1, y + r + 1) / area * 1.5);
      }
    }
    const ud = feather ? under.getImageData(0, 0, tileSize, tileSize).data : null;
    if (fit || feather) {
      for (let py = 0, i = 0; py < tileSize; py++) for (let px = 0; px < tileSize; px++, i += 4) {
        const f = feather ? feather[py * tileSize + px] : 0;
        for (let c = 0; c < 3; c++) {
          let v = d[i + c];
          if (fit) v = fit(px, py, c, v);
          d[i + c] = f > 0 ? v * (1 - f) + ud[i + c] * f : v;
        }
      }
      ctx.putImageData(image, 0, 0);
    }
    signal?.throwIfAborted();
    return new Response(await toBlob(out, 0.9), { headers: { 'content-type': 'image/jpeg' } });
  };
}
